import * as React from "react";
import { AlertDialog, AlertDialogContent, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import * as Icons from "lucide-react";
import { AlertDialogDescription } from "@radix-ui/react-alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner" 
export type SavedFilterBase = {
  id: string;
  user_id?: string;
  created_at?: string | null;
  updated_at?: string | null;
  filter_name?: string | null;
  filter?: Array<{ field: string; value: unknown; display: string }>;
};


export type SelectSavedFilterDialogProps<T extends SavedFilterBase> = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: T[];
  onApply: (selected: T) => void;
  onEdit?: (selected: T) => void;
  onDelete?: (selected: T) => void;
};

function toLabel(value: unknown) {
  if (value == null) return "-";
  if (typeof value === "string") return value.length > 64 ? value.slice(0, 61) + "…" : value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    const s = JSON.stringify(value);
    return s.length > 64 ? s.slice(0, 61) + "…" : s;
  } catch {
    return String(value);
  }
}

function useFiltered<T extends SavedFilterBase>(items: T[], query: string) {
  return React.useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter((f) => {
      const name = (f.filter_name ?? "").toLowerCase();
      const fields = (f.filter ?? [])
        .map((x) => `${x.field}:${toLabel(x.value)}`.toLowerCase())
        .join(" ");
      return name.includes(q) || fields.includes(q);
    });
  }, [items, query]);
}

export function SelectSavedFilterDialog<T extends SavedFilterBase>({
  open,
  onOpenChange,
  filters,
  onApply,
  onEdit,
  onDelete
}: SelectSavedFilterDialogProps<T>) {
  const [query, setQuery] = React.useState("");
  const [hoveredId, setHoveredId] = React.useState<string | null>(null);

  const filtered = useFiltered(filters, query);

  React.useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const handleApply = (item: T) => {
    onApply(item);
    toast.success("Filter toegepast");
    onOpenChange(false);
  };

  const handleEdit = (item: T) => {
    onOpenChange(false);
    onEdit?.(item);
  };

  const handleDelete = (item: T) => {
    onOpenChange(false);
    onDelete?.(item);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-3xl">
        <AlertDialogDescription></AlertDialogDescription>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <AlertDialogTitle className="text-lg font-semibold flex items-center gap-2">
              <Icons.Filter className="h-5 w-5" />
              Opgeslagen filters
            </AlertDialogTitle>
            {filters.length > 0 ? (
              <Badge variant="secondary" className="rounded-full">
                {filtered.length}/{filters.length}
              </Badge>
            ) : null}
          </div>

          <div className="relative">
            <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-60" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Zoek op naam, veld of waarde…"
              className="pl-9"
            />
          </div>

          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground">Geen filters gevonden.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-2">
              {filtered.map((f) => {
                const fields = f.filter ?? [];
                const shown = fields.slice(0, 6);
                const remaining = Math.max(0, fields.length - shown.length);

                return (
                  <div
                    key={f.id}
                    onMouseEnter={() => setHoveredId(f.id)}
                    onMouseLeave={() => setHoveredId((id) => (id === f.id ? null : id))}
                    onDoubleClick={() => handleApply(f)}
                    className="group border rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow cursor-default bg-card"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="font-medium truncate" title={f.filter_name ?? "Zonder naam"}>
                          {f.filter_name ?? "Zonder naam"}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {fields.length} veld{fields.length === 1 ? "" : "en"}
                        </p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger>
                          <Icons.MoreVertical className="h-4 w-4" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => handleEdit(f)}><Icons.Edit className="w-4 h-4"/> Bewerken</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(f)}><Icons.Trash className="w-4 h-4"/> Verwijderen</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                    </div>

                    <div className="mt-3 flex flex-wrap flex-col gap-1.5">
                      {shown.length === 0 ? (
                        <span className="text-xs text-muted-foreground">Geen velden</span>
                      ) : (
                        shown.map((ff, idx) => (
                          <Badge
                            key={`${f.id}-${ff.field}-${idx}`}
                            variant="outline"
                            className="text-[11px] font-normal px-2 py-1 border-none"
                            title={`${ff.field}: ${toLabel(ff.value)}`}
                          >
                            <span className="opacity-75 mr-1">{ff.field}</span>
                            <span className="font-medium">{toLabel(ff.display)}</span>
                          </Badge>
                        ))
                      )}
                      {remaining > 0 && (
                        <Badge variant="secondary" className="text-[11px] rounded-full">
                          +{remaining} meer
                        </Badge>
                      )}
                    </div>

                    <div className="mt-3 text-[11px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                      Dubbelklik om direct toe te passen
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
